import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiArchive, FiPlus, FiSave, FiTrash2 } from 'react-icons/fi';
import { createOffering } from '../../services/api';
import { today } from '../../utils/dateUtils';
import { fmtCurrency } from '../../utils/formatNumber';
import { OFFERING_LABEL } from './offeringsConfig';
import OfferingsMemberSelect from './OfferingsMemberSelect';
import OfferingsCategorySelect from './OfferingsCategorySelect';
import styles from './MultiOfferingsPage.module.css';
import rStyles from '../Receipts/NewReceiptsPage.module.css';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

const emptyRow = () => ({
  _id: `${Date.now()}-${Math.random()}`,
  offeringDate: today(),
  eventId: '',
  isEventDay: false,
  userId: '',
  userName: '',
  userEmail: '',
  userPhone: '',
  donationCategoryId: '',
  categoryName: '',
  qty: '',
  rate: '',
  amount: '',
  hasDefaultRate: false,
  manualAmount: false,
  notes: '',
});

const getFyForDate = (date, years) => {
  if (!date) return null;
  return years.find((fy) => {
    const start = (fy.startDate || '').slice(0, 10);
    const end = (fy.endDate || '').slice(0, 10);
    return start && end && date >= start && date <= end && !fy.isClosed;
  }) || null;
};

export default function MultiOfferingsPage({
  categories = [],
  events = [],
  personUsers = [],
  years = [],
  activeDerasarId,
  onClose,
  onSuccess,
}) {
  const [rows, setRows] = useState(() => [emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);

  const categoryOptions = useMemo(() =>
    categories.map((c) => ({ ...c, disabled: false, badge: null })),
    [categories],
  );

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => r._id === id ? { ...r, ...patch } : r));

  const removeRow = (id) =>
    setRows((rs) => rs.filter((r) => r._id !== id));

  const addRow = () =>
    setRows((rs) => [...rs, emptyRow()]);

  const matchingEventsForDate = useCallback((date) => {
    if (!date) return [];
    return events.filter((ev) => {
      const start = (ev.eventDate || '').slice(0, 10);
      const end = (ev.endDate || ev.eventDate || '').slice(0, 10);
      return start && end && date >= start && date <= end;
    });
  }, [events]);

  const handleDateChange = (rowId, date, currentEventId) => {
    const matching = matchingEventsForDate(date);
    const stillMatches = currentEventId && matching.some((e) => String(e.id) === String(currentEventId));
    updateRow(rowId, {
      offeringDate: date,
      eventId: stillMatches ? currentEventId : '',
      isEventDay: stillMatches,
    });
  };

  const handleMemberSelect = (rowId, member) => {
    updateRow(rowId, {
      userId: String(member.id),
      userName: member.name || '',
      userEmail: member.email || '',
      userPhone: member.phone || '',
    });
  };

  const handleCategorySelect = (rowId, cat) => {
    if (!cat) {
      updateRow(rowId, { donationCategoryId: '', categoryName: '', qty: '', rate: '', amount: '', hasDefaultRate: false, manualAmount: false });
      return;
    }
    const defaultRate = parseFloat(cat.defaultValue);
    const hasDefaultRate = Number.isFinite(defaultRate) && defaultRate > 0;
    const defaultQtyVal = parseInt(cat.defaultQty, 10);
    const hasDefaultQty = Number.isFinite(defaultQtyVal) && defaultQtyVal > 0;
    const qty = hasDefaultQty ? String(defaultQtyVal) : '';
    const rate = hasDefaultRate ? String(defaultRate) : '';
    const amount = hasDefaultRate && qty ? String(Math.round(parseFloat(qty) * defaultRate)) : '';
    updateRow(rowId, {
      donationCategoryId: String(cat.id),
      categoryName: cat.name || '',
      qty, rate, amount,
      hasDefaultRate, manualAmount: false,
    });
  };

  const handleQtyChange = (rowId, qty, row) => {
    const rate = parseFloat(row.rate);
    const computed = rate > 0 ? String(Math.round((parseFloat(qty) || 0) * rate)) : row.amount;
    updateRow(rowId, { qty, amount: computed, manualAmount: false });
  };

  const handleRateChange = (rowId, rate, row) => {
    const qty = parseFloat(row.qty) || 1;
    const computed = String(Math.round(qty * (parseFloat(rate) || 0)));
    updateRow(rowId, { rate, amount: computed, manualAmount: false });
  };

  const validRows = useMemo(() =>
    rows.filter((r) => r.userId && r.donationCategoryId && parseFloat(r.amount) > 0),
    [rows],
  );

  const totalAmount = useMemo(() =>
    rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [rows],
  );

  const handleSubmit = async () => {
    if (validRows.length === 0)
      return toast.error('Add at least one row with a member, category, and amount');

    const noFy = validRows.find((r) => !getFyForDate(r.offeringDate, years));
    if (noFy)
      return toast.error(`No open financial year covers ${noFy.offeringDate} — check dates or year locks`);

    setSaving(true);
    try {
      await Promise.all(
        validRows.map((r) =>
          createOffering({
            derasarId: activeDerasarId,
            userId: r.userId,
            donationCategoryId: r.donationCategoryId,
            financialYearId: getFyForDate(r.offeringDate, years).id,
            qty: parseInt(r.qty, 10) || null,
            defaultRate: parseFloat(r.rate) || null,
            offeredAmount: parseFloat(r.amount),
            offeringDate: r.offeringDate,
            isEventDay: !!r.isEventDay,
            eventId: r.eventId || undefined,
            notes: r.notes || undefined,
          }),
        ),
      );
      toast.success(`${validRows.length} ${validRows.length === 1 ? OFFERING_LABEL.singular : OFFERING_LABEL.plural} created`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create offerings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h2><FiArchive /> Multi {OFFERING_LABEL.singular} Entry</h2>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
          <FiArrowLeft /> Back
        </button>
      </div>

      <div className={styles.scrollArea}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thNum}>#</th>
                <th className={styles.thDate}>Date</th>
                <th className={styles.thEvent}>Event</th>
                <th className={styles.thMember}>Member *</th>
                <th className={styles.thCat}>Category *</th>
                <th className={styles.thQty}>Qty</th>
                <th className={styles.thRate}>Rate (₹)</th>
                <th className={styles.thAmt}>Amount (₹) *</th>
                <th className={styles.thNotes}>Notes</th>
                <th className={styles.thDel} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const matchingEvs = matchingEventsForDate(row.offeringDate);
                const isValid = row.userId && row.donationCategoryId && parseFloat(row.amount) > 0;
                return (
                  <tr key={row._id} className={`${styles.row} ${isValid ? styles.rowValid : ''}`}>

                    <td className={styles.tdNum}>{idx + 1}</td>

                    <td className={styles.tdDate}>
                      <input
                        type="date"
                        className={styles.cellInput}
                        value={row.offeringDate}
                        onChange={(e) => handleDateChange(row._id, e.target.value, row.eventId)}
                      />
                    </td>

                    <td className={styles.tdEvent}>
                      <select
                        className={styles.cellSelect}
                        value={row.eventId || ''}
                        disabled={matchingEvs.length === 0}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateRow(row._id, { eventId: val || '', isEventDay: !!val });
                        }}
                      >
                        <option value="">
                          {matchingEvs.length === 0 ? '—' : '— Select —'}
                        </option>
                        {matchingEvs.map((ev) => (
                          <option key={ev.id} value={String(ev.id)}>{ev.title}</option>
                        ))}
                      </select>
                    </td>

                    <td className={styles.tdMember}>
                      <OfferingsMemberSelect
                        value={row.userId}
                        displayValue={row.userName}
                        displayEmail={row.userEmail}
                        displayPhone={row.userPhone}
                        options={personUsers}
                        onSelect={(m) => handleMemberSelect(row._id, m)}
                        compact
                      />
                    </td>

                    <td className={styles.tdCat}>
                      <OfferingsCategorySelect
                        value={row.donationCategoryId}
                        displayValue={row.categoryName}
                        options={categoryOptions}
                        rowIndex={idx}
                        totalRows={rows.length}
                        onSelect={(c) => handleCategorySelect(row._id, c)}
                        panelStyle={{ minWidth: 300, right: 'auto' }}
                      />
                    </td>

                    <td className={styles.tdQty}>
                      <input
                        type="number" min="1" step="1"
                        className={styles.numInput}
                        value={row.qty}
                        onWheel={blockWheel}
                        onChange={(e) => handleQtyChange(row._id, e.target.value, row)}
                      />
                    </td>

                    <td className={styles.tdRate}>
                      <input
                        type="number" min="0" step="0.01"
                        className={styles.numInput}
                        value={row.rate}
                        onWheel={blockWheel}
                        onChange={(e) => handleRateChange(row._id, e.target.value, row)}
                      />
                    </td>

                    <td className={styles.tdAmt}>
                      <input
                        type="number" min="0" step="1"
                        className={`${styles.numInput} ${parseFloat(row.amount) > 0 ? styles.amtFilled : ''}`}
                        value={row.amount}
                        onWheel={blockWheel}
                        onChange={(e) => updateRow(row._id, { amount: e.target.value, manualAmount: true })}
                      />
                    </td>

                    <td className={styles.tdNotes}>
                      <input
                        className={styles.cellInput}
                        value={row.notes}
                        placeholder="Optional"
                        onChange={(e) => updateRow(row._id, { notes: e.target.value })}
                      />
                    </td>

                    <td className={styles.tdDel}>
                      <button
                        type="button"
                        className={styles.delBtn}
                        onClick={() => removeRow(row._id)}
                        disabled={rows.length === 1}
                        title="Remove row"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.addRowBar}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
            <FiPlus size={13} /> Add Row
          </button>
          {validRows.length > 0 && (
            <span className={styles.validCount}>
              {validRows.length} valid {validRows.length === 1 ? OFFERING_LABEL.singular.toLowerCase() : OFFERING_LABEL.plural.toLowerCase()} ready
            </span>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <div />
        <div className={styles.footerRight}>
          <div className={rStyles.totalBox}>
            <span className={rStyles.totalLabel}>Total {OFFERING_LABEL.singular} Amount</span>
            <span className={rStyles.totalValue}>{fmtCurrency(totalAmount)}</span>
          </div>
          <div className={rStyles.actionBtns}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || validRows.length === 0}
              onClick={handleSubmit}
            >
              {saving
                ? 'Saving…'
                : <><FiSave /> Create {validRows.length > 0 ? `${validRows.length} ` : ''}{validRows.length === 1 ? OFFERING_LABEL.singular : OFFERING_LABEL.plural}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
